import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../service/Api';

const ManageSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [isManagingBilling, setIsManagingBilling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch available plans
      const plansResponse = await apiService.getSubscriptionPlans();
      if (plansResponse.data?.success) {
        setPlans(plansResponse.data.data || []);
      }

      // Fetch current subscription
      const subResponse = await apiService.getCurrentSubscription();
      if (subResponse.data?.success) {
        setCurrentSubscription(subResponse.data.data);
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      setProcessingPlanId(planId);
      setError(null);

      const response = await apiService.createCheckoutSession(planId);

      if (response.data?.success) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else {
        setError(response.data?.error || 'Failed to create checkout session');
        setProcessingPlanId(null);
      }

    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err.response?.data?.error || 'Failed to start checkout process');
      setProcessingPlanId(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setIsManagingBilling(true);
      setError(null);

      const response = await apiService.createPortalSession();

      if (response.data?.success) {
        // Redirect to Stripe Billing Portal
        window.location.href = response.data.data.url;
      } else {
        setError(response.data?.error || 'Failed to open billing portal');
        setIsManagingBilling(false);
      }

    } catch (err) {
      console.error('Error opening portal:', err);
      setError(err.response?.data?.error || 'Failed to open billing portal');
      setIsManagingBilling(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/tailspin/dashboard')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Manage Subscription</h1>
          <p className="mt-2 text-gray-600">Manage your subscription plan and billing</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Current Plan Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          
          {currentSubscription ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-bold text-gray-900 capitalize">
                      {currentSubscription.plan?.name || 'Unknown'} Plan
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(currentSubscription.status)}`}>
                      {currentSubscription.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">
                    ${currentSubscription.plan?.price}/month
                  </p>
                </div>
                <button
                  onClick={handleManageSubscription}
                  disabled={isManagingBilling}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {isManagingBilling ? 'Loading...' : 'Manage Billing'}
                </button>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Collateral Limit</p>
                    <p className="font-medium">{currentSubscription.plan?.allowed_collaterals || 'N/A'} per month</p>
                  </div>
                  {currentSubscription.current_period_end && (
                    <div>
                      <p className="text-gray-500">Renews On</p>
                      <p className="font-medium">
                        {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {currentSubscription.cancel_at_period_end && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800">
                    ⚠️ Your subscription will be canceled at the end of the billing period.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Free Tier</h3>
              <p className="text-gray-600 mb-4">You're currently on the free tier. Upgrade to unlock more features!</p>
            </div>
          )}
        </div>

        {/* Available Plans */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {currentSubscription ? 'Available Plans' : 'Choose a Plan'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrentPlan = currentSubscription?.plan?.id === plan.id;
              
              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                    isCurrentPlan ? 'border-blue-500' : 'border-transparent'
                  }`}
                >
                  <div className="p-6">
                    {isCurrentPlan && (
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full mb-4">
                        Current Plan
                      </span>
                    )}
                    
                    <h3 className="text-2xl font-bold text-gray-900 capitalize mb-2">
                      {plan.name}
                    </h3>
                    
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600">/month</span>
                    </div>

                    <p className="text-gray-600 mb-6">
                      {plan.description || `Perfect for ${plan.name} users`}
                    </p>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">
                          {plan.allowed_collaterals} collaterals/month
                        </span>
                      </li>
                      {plan.features && plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 capitalize">
                            {feature.replace(/_/g, ' ')}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isCurrentPlan || processingPlanId === plan.id}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition ${
                        isCurrentPlan
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      } ${processingPlanId === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isCurrentPlan ? 'Current Plan' : processingPlanId === plan.id ? 'Processing...' : 'Subscribe Now'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 Need Help?</h3>
          <p className="text-blue-800">
            You can cancel or change your subscription at any time from the billing portal. 
            Questions? Contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscription;

